import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BlogSchemas, type CreateBlog } from "@workspace/contracts/blogs";
import type { SupportedLanguages } from "@workspace/contracts/language";
import { stripHtml } from "@workspace/core/strip-html";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { useAppForm } from "@/components/form-inputs";
import { AutoFillAltText } from "@/components/form-inputs/auto-fill-alt-text";
import { LanguageSelector } from "@/components/language-selector";
import { SeoDescriptionField, SeoTitleField } from "@/features/blogs/seo-fields";
import { SlugField } from "@/features/common/slug-field";
import { SeoUrlPreview } from "@/features/common/seo-url-preview";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/blogs/new")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const [lang, setLang] = useState<SupportedLanguages>("en");

  const { mutateAsync } = useMutation(orpc.admin.blogs.create.mutationOptions());

  const form = useAppForm({
    defaultValues: {
      published: false,
      slug: undefined,
      translations: {
        en: { title: "", content: "", image: undefined, altText: "", keywords: [] },
        ar: { title: "", content: "", image: undefined, altText: "", keywords: [] },
        tr: { title: "", content: "", image: undefined, altText: "", keywords: [] },
        fa: { title: "", content: "", image: undefined, altText: "", keywords: [] },
      },
    } as CreateBlog,

    validators: {
      onSubmit: BlogSchemas.create,
    },

    onSubmit: async ({ value }) => {
      try {
        await mutateAsync(value);
        toast.success("Blog was created successfully!");
        queryClient.invalidateQueries({ queryKey: orpc.admin.blogs.list.queryKey({ input: {} }) });
        navigate({ to: "/blogs" });
      } catch (error) {
        const msg = getError(error, "Failed to create blog");
        toast.error(msg);
      }
    },
    onSubmitInvalid: ({ formApi }) => {
      console.log(formApi.getAllErrors());
    },
  });

  const onSubmitHandler = (e: React.SubmitEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };
  return (
    <Main>
      <h1 className="mb-2 text-lg">Create New Blog</h1>

      <form onSubmit={onSubmitHandler} className="space-y-4">
        <div className="flex w-full items-center justify-between">
          <LanguageSelector lang={lang} setLang={setLang} />
        </div>

        <form.Subscribe selector={(state) => ({ title: state.values.translations[lang].title, altText: state.values.translations[lang].altText ?? "" })}>
          {({ title, altText }) => (
            <AutoFillAltText
              title={title}
              altText={altText}
              setAltText={(value) => form.setFieldValue(`translations.${lang}.altText`, value)}
            />
          )}
        </form.Subscribe>

        <form.AppField name={`translations.${lang}.title`}>
          {(titleField) => <titleField.TextField label="Title" placeholder="Top 10 things that didn't happen!" />}
        </form.AppField>

        <form.Subscribe selector={(state) => ({ title: state.values.translations[lang].title, content: state.values.translations[lang].content })}>
          {({ title, content }) => (
            <div className="space-y-4 rounded-lg border p-4">
              <p className="font-medium">SEO</p>
              <form.AppField name={`translations.${lang}.metaTitle`}>
                {() => (
                  <SeoTitleField
                    description="Defaults to the article title. Enter a value to override it."
                    dir={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                    fallback={title}
                    label="SEO Title"
                  />
                )}
              </form.AppField>
              <form.AppField name={`translations.${lang}.metaDescription`}>
                {() => (
                  <SeoDescriptionField
                    description="Defaults to the first 160 characters of the article content. Enter a value to override it."
                    dir={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
                    fallback={stripHtml(content).slice(0, 160)}
                    label="SEO Description"
                  />
                )}
              </form.AppField>
            </div>
          )}
        </form.Subscribe>

        <form.Subscribe selector={(state) => ({ slug: state.values.translations[lang].slug, title: state.values.translations[lang].title })}>
          {({ slug, title }) => (
            <>
              <form.AppField name={`translations.${lang}.slug`}>
                {(field) => <SlugField locale={lang} name={title} onBlur={field.handleBlur} onChange={field.handleChange} value={field.state.value} />}
              </form.AppField>
              <SeoUrlPreview locale={lang} slug={slug} title={title} type="blog" />
            </>
          )}
        </form.Subscribe>
        <p className="-mt-2 text-muted-foreground text-sm">
          The URL is generated on first save and stays unchanged when the title changes, unless you edit this field manually.
        </p>

        <form.AppField name={`translations.${lang}.keywords`}>
          {(field) => <field.TagField label="Keywords" />}
        </form.AppField>

        <form.AppField name={`translations.${lang}.publicationStatus`}>
          {(field) => <field.SelectField label="Translation publication status" options={[{ label: "Draft", value: "draft" }, { label: "Published", value: "published" }]} />}
        </form.AppField>

        <form.AppField name={`translations.${lang}.content`}>
          {(contentField) => (
            <contentField.EditorField
              direction={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}
              label="Content"
              placeholder="Top 10 things that didn't happen!"
              preview
            />
          )}
        </form.AppField>

        <form.AppField name={`translations.${lang}.image`}>
          {(field) => <field.ImageField label="Image" />}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.translations[lang].image}>
          {(image) => (
            <form.AppField name={`translations.${lang}.altText`}>
              {(field) => <field.TextField disabled={!image} label="Alt Text (auto-filled from the title)" type="text" />}
            </form.AppField>
          )}
        </form.Subscribe>

        <div className="mt-8 flex items-center justify-end gap-6">
          <div>
            <form.AppField name="published">{(field) => <field.CheckboxField label="Published" />}</form.AppField>
          </div>
          <form.AppForm>
            <form.SubmitButton>Submit</form.SubmitButton>
          </form.AppForm>
        </div>
      </form>
    </Main>
  );
}
